/* @flow */

import * as React from 'react';
import { observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import { translate } from 'react-i18next';

//----------------------------------------------------------------------//
// MATERIAL UI                                                          //
//----------------------------------------------------------------------//

import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import Typography from '@material-ui/core/Typography';

//----------------------------------------------------------------------//
//----------------------------------------------------------------------//

import AutoSizer from 'react-virtualized/dist/commonjs/AutoSizer';
import { List as VirtualList } from 'react-virtualized/dist/commonjs/List';

import type { RowRendererParams } from 'react-virtualized/dist/commonjs/List';

import type {
  TypeManufacturersProductsStore,
  TypePropsManufacturers,
} from 'stores/ManufacturersProductsStore.js';

import AddNewLink from 'components/AddNewLink/AddNewLink';
import finderColumns from 'components/FinderColumns/FinderColumns';
import type {
  FinderColumnInjectedProps,
  FinderColumnProps,
} from 'components/FinderColumns/FinderColumns';
import Loading from 'components/Loading/Loading';
import { ComponentWithInjectedProps } from 'utils/injectedProps';
import * as statuses from 'utils/statuses';

import styles from './ManufacturerList.module.scss';

type InjectedProps = {
  manufacturersProductsStore: TypeManufacturersProductsStore,
} & FinderColumnInjectedProps;
type Props = {
  listTitle: string,
  manufacturerStatus: Array<string>,
} & FinderColumnProps;

@translate()
@inject('manufacturersProductsStore')
@finderColumns
@observer
export default class ManufacturerList extends ComponentWithInjectedProps<Props, {}, InjectedProps> {
  @observable expanded = 'Approved';

  @observable activeListItem = {
    index: null,
    expanded: this.expanded,
  };

  manufacturersFilteredByStatus = (currentStatus: string) => {
    return this.props.columnData.filter((e: Object) => e.status === currentStatus);
  };

  rowRenderer = (vListProps: RowRendererParams) => {
    const currentStatus: string = vListProps.parent.props.currentStatus;
    const manufacturers: Array<*> = this.manufacturersFilteredByStatus(currentStatus);
    const row: TypePropsManufacturers = manufacturers[vListProps.index];
    const manufacturerId: number = row.id;
    const manufacturerName: string = row.name;
    const rowCount: number = this.manufacturersFilteredByStatus(currentStatus).length;
    const index: number = vListProps.index;
    const activeStyle: string =
      currentStatus === this.activeListItem.expanded && index === this.activeListItem.index
        ? styles['list-active']
        : '';

    return (
      <ListItem
        key={vListProps.key}
        button={false}
        divider={false}
        onClick={(ev: SyntheticInputEvent<*>) =>
          this.handleManufacturerClick(
            ev,
            vListProps,
            rowCount,
            manufacturerId,
            manufacturerName,
            currentStatus
          )
        }
        className={`${styles['list-item']} ${activeStyle}`}
        style={vListProps.style}
      >
        {row.name}
      </ListItem>
    );
  };

  handleExpandedChange = (panel: string) => (ev: SyntheticInputEvent<*>, expanded: boolean) => {
    this.expanded = expanded ? panel : false;
  };

  handleManufacturerClick = (
    ev: SyntheticInputEvent<*>,
    vListProps: RowRendererParams,
    rowCount: number,
    manufacturerId: number,
    manufacturerName: string,
    currentStatus: string
  ) => {
    const manufacturersProductsStore: TypeManufacturersProductsStore = this.props
      .manufacturersProductsStore;
    const currentItem: Object = ev.currentTarget;
    const activeItems: Object = currentItem.parentNode.getElementsByClassName(
      styles['list-active']
    );

    [].forEach.call(activeItems, function(el: Object) {
      el.classList.remove(styles['list-active']);
    });

    currentItem.classList.add(styles['list-active']);
    this.activeListItem = {
      index: vListProps.index,
      expanded: currentStatus,
    };

    manufacturersProductsStore.setSelectedManufacturerId(manufacturerId);
  };

  handleAddNewManufacturer = () => {};

  render() {
    const { manufacturerStatus, manufacturersProductsStore, t, i18n } = this.props;

    return (
      <List className={styles['expandable-container']} disablePadding={true} component="div">
        {manufacturersProductsStore.loadingManufacturers ? (
          <div className={styles['loading-wrapper']}>
            <Loading />
          </div>
        ) : (
          <React.Fragment>
            <AddNewLink
              linkTo="/manufacturers/create"
              linkText={t('general:actions.add_new_manufacturer')}
              permissions={null}
            />

            {manufacturerStatus.map((currentStatus: string) => {
              return (
                <ExpansionPanel
                  className={`${styles['expansion-panel']}
                                         ${this.expanded === currentStatus ? styles.expanded : ''}`}
                  key={currentStatus}
                  elevation={0}
                  expanded={this.expanded === currentStatus}
                  onChange={this.handleExpandedChange(currentStatus)}
                >
                  <ExpansionPanelSummary
                    className={styles['expansion-panel-header']}
                    expandIcon={
                      <svg className={styles['icon-chevron']}>
                        <use
                          xlinkHref={
                            this.expanded === currentStatus
                              ? '#icon-chevron-down'
                              : '#icon-chevron-up'
                          }
                        />
                      </svg>
                    }
                  >
                    <Typography>
                      {statuses.translate(currentStatus, i18n)} (
                      {this.manufacturersFilteredByStatus(currentStatus).length})
                    </Typography>
                  </ExpansionPanelSummary>
                  {this.expanded === currentStatus ? (
                    <ExpansionPanelDetails className={styles['auto-sizer-wrapper']}>
                      <AutoSizer>
                        {({ width, height }: Object) => (
                          <VirtualList
                            height={height}
                            rowCount={this.manufacturersFilteredByStatus(currentStatus).length}
                            rowHeight={36}
                            rowRenderer={this.rowRenderer}
                            width={width}
                            currentStatus={currentStatus}
                          />
                        )}
                      </AutoSizer>
                    </ExpansionPanelDetails>
                  ) : null}
                </ExpansionPanel>
              );
            })}
          </React.Fragment>
        )}
      </List>
    );
  }
}
